// CollaborationContactsQuadrant — Top-right quadrant: Contacts with multi-select + alphabet filter
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Plus, Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCollaborationFilter } from '../../hooks/useCollaborationFilter'
import type { DetailItem } from '../../types/collaboration'

const PAGE_SIZE = 8
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

interface Props {
  onDetailClick: (item: DetailItem) => void
}

export default function CollaborationContactsQuadrant({ onDetailClick }: Props) {
  const { filteredContacts, selectedContactIds, toggleContact, contactAlphabetFilter, contactAvailableLetters, toggleContactLetter, clearContactAlphabet } = useCollaborationFilter()
  const navigate = useNavigate()
  const [page, setPage] = useState(0)

  const totalPages = Math.ceil(filteredContacts.length / PAGE_SIZE)
  const safePage = Math.min(page, Math.max(0, totalPages - 1))
  const pagedContacts = filteredContacts.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  useEffect(() => { setPage(0) }, [filteredContacts.length])

  return (
    <div className="light-card flex flex-col" style={{ maxHeight: '420px' }}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-sky-200/50">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-iflow-600" />
          <h3 className="text-sm font-semibold text-gray-900">Contacts</h3>
          <span className="px-1.5 py-0.5 text-xs rounded-full bg-sky-500/10 text-gray-600 font-medium">{filteredContacts.length}</span>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => navigate('/crm/contacts')}
            title="Add a contact in My Contacts"
            className="flex items-center gap-1 px-2 py-1 text-xs bg-iflow-700 text-white rounded-lg hover:bg-iflow-800"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
      </div>
      {/* Inline alphabet filter — by surname */}
      <div className="flex flex-wrap items-center gap-[3px] px-3 py-1.5 border-b border-gray-100">
        {ALPHABET.map(letter => {
          const isAvailable = contactAvailableLetters.has(letter)
          const isSelected = contactAlphabetFilter.has(letter)
          return (
            <button key={letter} disabled={!isAvailable} onClick={() => toggleContactLetter(letter)}
              className={`w-5 h-5 text-[10px] font-medium rounded transition-colors ${
                isSelected ? 'bg-iflow-700 text-white' : isAvailable ? 'text-gray-600 hover:bg-iflow-100' : 'text-gray-300 cursor-not-allowed'
              }`}>{letter}</button>
          )
        })}
        {contactAlphabetFilter.size > 0 && (
          <button onClick={clearContactAlphabet} className="ml-1 text-[10px] text-iflow-600 hover:underline">Clear</button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2 min-h-0">
        {filteredContacts.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No contacts linked yet.</p>
        ) : (
          <div className="space-y-1">
            {pagedContacts.map((contact) => {
              const isSelected = selectedContactIds.has(contact.id)
              return (
                <div key={contact.id}
                  title={`Why is ${contact.name} here? ${contact.source ? contact.source : 'Added via directory match.'}`}
                  className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors ${
                    isSelected ? 'bg-iflow-50 border-l-3 border-iflow-500' : 'hover:bg-sky-500/5'
                  }`}>
                  <div onClick={() => toggleContact(contact.id)} className="cursor-pointer flex-shrink-0">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-iflow-500 border-iflow-500' : 'border-gray-300'
                    }`}>{isSelected && <Check className="w-3 h-3 text-white" />}</div>
                  </div>
                  <div onClick={() => onDetailClick({ type: 'contact', item: contact })} className="flex-1 min-w-0 cursor-pointer">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-gray-900 truncate">{contact.name}</p>
                      {contact.notes?.startsWith('source: ai_') && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-50 text-blue-600 font-medium shrink-0">AI</span>
                      )}
                      {contact.centralDirectoryId && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-teal-100 text-teal-700 shrink-0">Directory</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{contact.role}{contact.email ? ` \u00b7 ${contact.email}` : ''}</p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-1.5 border-t border-gray-100 text-xs text-gray-500">
          <button disabled={safePage === 0} onClick={() => setPage(p => p - 1)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          <span>{safePage + 1} of {totalPages}</span>
          <button disabled={safePage >= totalPages - 1} onClick={() => setPage(p => p + 1)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  )
}
